import axios from 'axios';
import 'dayjs/locale/th';
import parse from 'html-react-parser';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import { NextSeo } from 'next-seo';
import SEO from 'next-seo.config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PageError from 'pages/404';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout, { processCategory } from 'src/components/layouts';
import styled from 'styled-components';
import {
  API_URI,
  APP_IMG,
  APP_URL,
  CLIENT_GSERVICE_URL,
  // NEXT_PUBLIC_CLOSE_TRANSLATE,
  NEXT_PUBLIC_URL_SOUND,
  NEXT_PUBLIC_WEB_NAME_SOUND,
  SHOW_IMAGE_DESC_ID,
  STICKY_AD_CATEGORIES,
  STICKY_AD_DURATION
} from 'src/constants';
import { IMAGE_DEFAULT, STATIC_FILE, WEB_DESC, WEB_KEYWORD, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { convertObjPath, escapeChar, rewriteTxt } from 'src/utils/helper';

import { useMediaQuery } from 'react-responsive';
// Moved to dynamic imports below

import Script from 'next/script';
// Moved to dynamic import below
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { KEY_ADS_POSITION } from 'src/utils/ads/dynamic-ads';
import { setAds } from 'src/utils/contents/set-ads';
import { setContents } from 'src/utils/contents/set-contents';
import { useDesktop } from 'src/utils/device';
import useResponsive from 'src/utils/devices';
import { GET, GET_ARTICLES } from 'src/utils/services';
import useUser from 'src/utils/useUser';

// Lazy load components to reduce initial bundle and improve INP
const DynamicAds = dynamic(() => import('src/utils/ads/dynamic-ads'), {
  ssr: false,
  loading: () => null
});
// ArticleDefault must be SSR-enabled for LCP image discoverability
const SectionLandingPageForArticle = dynamic(() => import('src/components/sections/section-landing-page-for-article'), { ssr: false });

const ArticleDefaultLazy = dynamic(() => import('src/2.0/articles/article-default'), {
  ssr: true,
  loading: () => <div style={{ minHeight: '400px' }} />
});
const WidgetStickyContentsLazy = dynamic(() => import('src/2.0/translates/widget-sticky-contents'), {
  ssr: false,
  loading: () => <WidgetStickyPlaceholder />
});
const SectionAffiliateLazy = dynamic(() => import('src/components/sections/section-event/section-affiliate'), {
  ssr: false,
  loading: () => null
});
const PageCategoryDetail = props => {
  const { article, related, lastNews, isTranslated, dataAffiliate, dataAdsNative, ads, dataAdsHeader, dataLandingPage, navData } = props;
  const _data = useUser();
  const sessionSevId = _data?.user ? decryptionClient(_data?.user) : null;
  // const userId = getCookie('pdpa_user_id');
  // const userId = getCookie('pdpa_user_id');
  // const interactionSent = useRef(false);

  if (!article) {
    return <PageError dataAdsHeader={dataAdsHeader} sessionSevId={sessionSevId} />;
  }
  const [isTranslate, setIsTranslate] = useState(false);
  const [isKeyPoint, setIsKeyPoint] = useState(true);
  const [selectLang, setSelectLang] = useState('TH');
  const [newsArticle, setNewsArticle] = useState({ ...article });
  const router = useRouter();
  const responsive = useResponsive();

  // const isDesktop = useDesktop();
  const [adsBox, setAdsBox] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const isDesktop = useDesktop();
  const isStickyAdCategory = STICKY_AD_CATEGORIES?.includes(article?.section?.en);

  const sentinelRef = useRef(null); // sentinel วางไว้เหนือ billboard เพื่อดักจับจังหวะ "เริ่ม stick"
  const stickyTimeoutRef = useRef(null);
  const hasStuckRef = useRef(false);
  const isStuckRef = useRef(false);
  const [stickyMenuHeight, setStickyMenuHeight] = useState(0); // ความสูงเมนู sticky เดิม
  const [isReleased, setIsReleased] = useState(false); // true = ปลด sticky หลังครบ 5 วิ
  const [isStuck, setIsStuck] = useState(false);

  const adsRef = useRef(null);
  const [adsHeight, setAdsHeight] = useState(null);

  useEffect(() => {
    isStuckRef.current = isStuck && !isReleased;
  }, [isStuck, isReleased]);

  useEffect(() => {
    const headerEl = document.querySelector('.header-desktop');
    const updateMenuHeight = () => setStickyMenuHeight(headerEl ? headerEl.offsetHeight : 0);
    updateMenuHeight();
    window.addEventListener('resize', updateMenuHeight);
    return () => window.removeEventListener('resize', updateMenuHeight);
  }, []);

  useEffect(() => {
    if (!adsRef.current) return;

    const ro = new ResizeObserver(entries => {
      if (isStuckRef.current) return;
      const h = entries[0]?.contentRect?.height;
      if (h && h > 0) setAdsHeight(h);
    });
    ro.observe(adsRef.current);

    return () => ro.disconnect();
  }, [ads]);

  useEffect(() => {
    if (!isDesktop || !isStickyAdCategory || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          if (!hasStuckRef.current) {
            hasStuckRef.current = true;
            setIsStuck(true);
            setIsReleased(false);

            if (stickyTimeoutRef.current) clearTimeout(stickyTimeoutRef.current);
            stickyTimeoutRef.current = setTimeout(() => {
              setIsReleased(true);
              setIsStuck(false);
            }, STICKY_AD_DURATION);
          }
        } else {
          hasStuckRef.current = false;
          setIsStuck(false);
          setIsReleased(false);
          if (stickyTimeoutRef.current) {
            clearTimeout(stickyTimeoutRef.current);
            stickyTimeoutRef.current = null;
          }
        }
      },
      { rootMargin: `-${stickyMenuHeight}px 0px 0px 0px`, threshold: 0 }
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
      if (stickyTimeoutRef.current) clearTimeout(stickyTimeoutRef.current);
    };
  }, [isDesktop, isStickyAdCategory, stickyMenuHeight]);

  const handleLightBoxClick = () => {
    // Use requestAnimationFrame to defer state update
    requestAnimationFrame(() => {
      setAdsBox(false);
    });
  };

  // Memoize article object to prevent unnecessary recalculations
  const _article = useMemo(
    () => ({
      ...article,
      ...newsArticle,
      title: newsArticle?.meta?.title,
      description: newsArticle?.meta?.description || WEB_DESC,
      keyword: newsArticle?.meta?.keyword || WEB_KEYWORD,
      id: newsArticle?.id,
      link: newsArticle?.link,
      tags: newsArticle?.tags,
      section: newsArticle?.section,
      image_url: newsArticle?.meta?.og_social_image,
      subCategory: newsArticle?.subCategory
    }),
    [article, newsArticle]
  );
  // Load Instagram embed.js only when the article actually contains an IG embed (most don't) — saves a main-thread script parse → better INP
  const hasInstagramEmbed = useMemo(() => {
    const body = `${_article?.content_I || ''}${_article?.content_II || ''}`;
    return body.includes('instagram.com') || body.includes('instagram-media');
  }, [_article?.content_I, _article?.content_II]);

  // Memoize computed values
  const baseUrlCategory = useMemo(() => `${_article?.canonical}`, [_article?.canonical]);
  const articleTitle = useMemo(() => escapeChar(_article?.title), [_article?.title]);
  // const categoryName = _article?.subCategory?.[isTranslate ? 'en' : 'th'] ? _article?.subCategory?.[isTranslate ? 'en' : 'th'] : _article?.section?.[isTranslate ? 'en' : 'th'];
  // const categoryPath = _article?.subCategory?.en ? `${_article?.section?.en}/${_article?.subCategory?.en}` : _article?.section?.en;

  /* -------------------------------- DATALAYER ------------------------------- */

  // const nextSectionIndexRef = useRef(0);
  // const sectionsVisibleRef = useRef([]);
  // const sectionRef = useRef(null);
  // const footerRef = useRef(false);
  const handleTranslate = useCallback(
    async newLanguage => {
      if (isLoading) return;

      // Immediate UI feedback - batch state updates
      setSelectLang(newLanguage);
      setIsTranslate(prev => !prev);
      setIsKeyPoint(false);

      // Defer heavy operations
      requestAnimationFrame(() => {
        setIsLoading(true);
      });

      try {
        if (NEXT_PUBLIC_URL_SOUND !== undefined || !isEmpty(_article?.id)) {
          const uriToken = `${NEXT_PUBLIC_URL_SOUND}/create`;
          const getToken = await axios
            .post(
              uriToken,
              {
                app: 'default'
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'User-Agents': `NationSpeech/1.0 ${props?.agent}`
                }
              }
            )
            .catch(error => {
              console.error(error);
            });

          // console.log({ getToken });
          if (getToken?.data?.data?.token !== undefined) {
            const uriEn = `${NEXT_PUBLIC_URL_SOUND}/translates/${_article?.id}/${newLanguage}`;
            // console.log('uriEn', uriEn);

            const translate = await axios
              .post(
                uriEn,
                {
                  data: {
                    id: _article?.id,
                    site: NEXT_PUBLIC_WEB_NAME_SOUND,
                    language: newLanguage
                  }
                },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'User-Agents': `NationSpeech/1.0 ${props?.agent}`,
                    accessToken: getToken?.data?.data?.token,
                    accessTimeStamp: getToken?.data?.data?.timestamp
                  }
                }
              )
              .catch(error => {
                console.error(error);
              });
            await new Promise(resolve => setTimeout(resolve, 500));
            setNewsArticle({
              ...article,
              ...translate?.data?.data
            });
          }
        }
      } catch (error) {
        setIsLoading(true);
        console.log(error, 'Error Translate ');
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, _article?.id, article, props?.agent]
  );

  useEffect(() => {
    // Defer non-critical localStorage operations
    const timer = setTimeout(() => {
      try {
        localStorage.setItem('_t', '0');
        let timeCount = 0;
        const increment = () => {
          timeCount += 1;
          // อัพเดท localStorage ทุก 30 วินาทีเพื่อลด overhead
          if (timeCount % 30 === 0) {
            localStorage.setItem('_t', timeCount.toString());
          }
        };
        const intervalId = setInterval(increment, 1000);
        return () => clearInterval(intervalId);
      } catch (err) {
        console.log('Error: Please enable website cookies. ' + err);
      }
    }, 2000); // Defer by 2 seconds

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Defer non-critical operations using requestIdleCallback
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        Number(_article?.id) > Number(SHOW_IMAGE_DESC_ID) && setContents();
        setAds();
      });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        Number(_article?.id) > Number(SHOW_IMAGE_DESC_ID) && setContents();
        setAds();
      }, 100);
    }

    // Defer analytics call - fire and forget, no state update needed
    const fetchCountView = async () => {
      const params = {
        site: 'bangkokbiznews',
        newsid: _article.id
      };
      try {
        await fetch(`${CLIENT_GSERVICE_URL}/cpvs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(params)
        });
        // No need to update state - just fire and forget
      } catch (err) {
        // Silent fail
      }
    };

    // Defer by 3 seconds to not block main thread
    const timer = setTimeout(fetchCountView, 3000);
    return () => clearTimeout(timer);
  }, [_article.id]);
  // handleCommemorate
  function getScrollTop() {
    if (typeof window.pageYOffset !== 'undefined') {
      // Most browsers
      return window.pageYOffset;
    }

    const d = document.documentElement;
    if (typeof d.clientHeight !== 'undefined') {
      // IE in standards mode
      return d.scrollTop;
    }

    // IE in quirks mode
    return document.body.scrollTop;
  }
  // Memoize media query to prevent recalculation
  const isMb = useMediaQuery({ query: '(max-width: 768px)' });
  useEffect(() => {
    if (!isMb) return;

    // Defer scroll listener setup
    const setupScrollListener = () => {
      let ticking = false;
      let lastScrollTop = 0;
      const threshold = 10; // Increase threshold to 10px for less frequent updates
      let boxCache = null;
      let boxJsCache = null;

      const handleScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(() => {
            const scroll = getScrollTop();

            // Only update if scroll changed significantly
            if (Math.abs(scroll - lastScrollTop) < threshold) {
              ticking = false;
              return;
            }

            lastScrollTop = scroll;

            // Cache DOM queries
            if (!boxCache) boxCache = document.getElementById('widget-group');
            if (!boxJsCache) boxJsCache = document.getElementById('widget-group-js');

            if (boxCache || boxJsCache) {
              const topValue = scroll <= 20 ? '112px' : '70px';
              if (boxCache) boxCache.style.top = topValue;
              if (boxJsCache) boxJsCache.style.top = topValue;
            }
            ticking = false;
          });
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    };

    // Defer setup by 1.5 seconds
    const timer = setTimeout(setupScrollListener, 1500);
    return () => clearTimeout(timer);
  }, [isMb]);

  // Memoize authors array to prevent recreation on every render
  const authors = useMemo(
    () => [
      {
        '@type': article?.byline ? 'Person' : 'Organization',
        name: article?.byline || 'กรุงเทพธุรกิจ',
        url: article?.byline ? APP_URL + '/bylines/' + article?.byline : APP_URL + '/bylines/' + 'กรุงเทพธุรกิจ'
      }
    ],
    [article?.created_by]
  );

  const articleDescription = useMemo(() => escapeChar(_article?.description), [_article?.description]);

  const isAnalysisSection = useMemo(() => ['economics', 'business']?.includes(_article?.section?.en), [_article?.section?.en]);

  const sectionCapitalized = useMemo(() => {
    const s = _article?.section?.en || '';
    return s.charAt(0)?.toUpperCase() + s?.slice(1);
  }, [_article?.section?.en]);

  const analysisSchemaFields = useMemo(() => {
    if (!isAnalysisSection) return '';
    return `
                  "articleSection": "${sectionCapitalized}",
                  "about": [
                    {
                      "@type": "Thing",
                      "name": "${sectionCapitalized} Analysis"
                    }
                  ],`;
  }, [isAnalysisSection, sectionCapitalized]);

  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        {...SEO}
        title={_article?.title}
        description={_article?.description}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
        openGraph={{
          ...SEO.openGraph,
          title: _article?.title,
          description: _article?.description,
          url: baseUrlCategory,
          article: {
            publishedTime: _article?.published_at,
            modifiedTime: _article?.updated_at,
            expirationTime: null,
            section: _article?.section?.th,
            authors: [`${WEB_URL.FACEBOOK}`],
            tags: _article?.tags
          },
          images: [
            {
              url: _article?.image_url,
              width: '1200',
              height: '675',
              alt: _article?.title
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article?.keyword
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlCategory} />

        {/* DNS prefetch for external resources */}
        <link rel='dns-prefetch' href='//securepubads.g.doubleclick.net' />
        <link rel='dns-prefetch' href='//www.googletagservices.com' />
        <link rel='dns-prefetch' href='//www.instagram.com' />

        {/* Preload critical LCP image — must match the URL actually rendered by <Image type='FEATURE_IMAGE' /> (webp + style/LG) */}
        {_article?.image && <link rel='preload' as='image' href={`${APP_IMG}${_article.image}?x-image-process=style/LG-webp`} type='image/webp' fetchpriority='high' />}

        {/* Preload Sarabun (เนื้อหาบทความ) — subset thai + latin, weight 300/400 เพื่อตัด CLS จากการ swap ฟอนต์เฉพาะหน้าบทความ. หมายเหตุ: ผูกกับ gstatic v17 ต้องอัปเดต URL หาก Google bump เวอร์ชัน */}
        <link rel='preload' as='font' type='font/woff2' href='https://fonts.gstatic.com/s/sarabun/v17/DtVmJx26TKEr37c9YL5rik8s6yLUrwB0lw.woff2' crossOrigin='anonymous' />
        <link rel='preload' as='font' type='font/woff2' href='https://fonts.gstatic.com/s/sarabun/v17/DtVmJx26TKEr37c9YL5rilss6yLUrwA.woff2' crossOrigin='anonymous' />
        <link rel='preload' as='font' type='font/woff2' href='https://fonts.gstatic.com/s/sarabun/v17/DtVjJx26TKEr37c9aAFJn3YO5gjupg.woff2' crossOrigin='anonymous' />
        <link rel='preload' as='font' type='font/woff2' href='https://fonts.gstatic.com/s/sarabun/v17/DtVjJx26TKEr37c9aBVJn3YO5gg.woff2' crossOrigin='anonymous' />

        <meta property='twitter:title' content={_article?.title || WEB_TITLE} />
        <meta property='twitter:description' content={_article?.description || WEB_DESC} />
        <meta property='twitter:image' content={!isEmpty(_article?.image) ? `${APP_IMG}${_article?.image}` : IMAGE_DEFAULT} />
        {/* ----------------------------- ScriptBrowser ------------------------------------ */}
        {!useDesktop() && !isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_mobile}`) : ''}
        {useDesktop() && !isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_desktop}`) : ''}
        {/* ----------------------------- ADS : PPN ------------------------------- */}
        <script async defer type='text/javascript' src='https://securepubads.g.doubleclick.net/tag/js/gpt.js' />
        <script
          type='text/javascript'
          defer
          dangerouslySetInnerHTML={{
            __html: 'var googletag = googletag || {}; window.googletag.cmd = googletag.cmd || [];'
          }}
        />
        {/* --------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                __th_page="${_article?.section?.en}";
              `
          }}
        />

        {/* ---------------------------- SPIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "https://schema.org",
                  "@type": "WebSite",
                  "name": "${WEB_NAME}",
                  "url": "${WEB_URL.WEB}",
                  "description": "${articleDescription || WEB_DESC}",
                  "sameAs": [
                    "${WEB_URL.FACEBOOK}",
                    "${WEB_URL.TWITTER}",
                    "${WEB_URL.INSTAGRAM}",
                    "${WEB_URL.YOUTUBE}",
                    "${WEB_URL.LINE}"
                  ],
                  "potentialAction": {
                    "@type": "SearchAction",
                    "target": "${WEB_URL.WEB}search?q={search_term_string}",
                    "query-input": "required name=search_term_string"
                  }
                }
              `
          }}
        />

        {/* ---------------------------- SPIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                { 
                  "@context": "http://schema.org", 
                  "@type": "WebPage", 
                  "name": "${articleTitle}", 
                  "description": "${articleDescription}", 
                  "publisher": { 
                    "@type": "ProfilePage", 
                    "name": "${WEB_NAME}" 
                  } 
                } 
              `
          }}
        />

        {/* ------------------------ SPIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                { 
                  "@context": "http://schema.org", 
                  "@type": "BreadcrumbList", 
                  "itemListElement": [ 
                    { 
                      "@type": "ListItem", 
                      "position": 1, 
                      "name": "หน้าแรก", 
                      "item": "${APP_URL}" 
                    }, 
                    { 
                      "@type": "ListItem", 
                      "position": 2, 
                      "name": "${_article?.section?.th}", 
                      "item": "${APP_URL}/${_article?.section?.en}" 
                    }, 
                    { 
                      "@type": "ListItem", 
                      "position": 3, 
                      "name": "${articleTitle}", 
                      "item": "${_article?.canonical || (!isEmpty(_article?.link) && `${APP_URL}${_article?.link}`) || `${APP_URL}/${_article?.section?.en}/${_article?.id}`}" 
                    } 
                  ] 
                } 
              `
          }}
        />

        {/* -------------------------- SPIPPET : NewsArticle ------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                { 
                  "@context": "http://schema.org", 
                  "@type": "${_article?.section?.en === 'economics' || _article?.section?.en === 'business' ? 'AnalysisNewsArticle' : 'NewsArticle'}", 
                  "mainEntityOfPage": { 
                    "@type": "WebPage", 
                    "@id": "${_article?.canonical || (!isEmpty(_article?.link) && `${APP_URL}${_article?.link}`) || `${APP_URL}/${_article?.section?.en}/${_article?.id}`}" 
                  }, 
                  "headline": "${articleTitle}", 
                  "alternativeHeadline": "${articleTitle}", 
                  "description": "${articleDescription}", ${analysisSchemaFields}
                  "image": "${APP_IMG}${_article?.image}", 
                  "datePublished": "${_article?.published_at}",
                  "dateModified": "${_article?.updated_at}",
                  "author": ${JSON.stringify(authors)}, 
                  "publisher": { 
                    "@type": "Organization", 
                    "name": "${WEB_NAME}", 
                    "logo": { 
                      "@type": "ImageObject", 
                      "url": "${APP_IMG}/static/${STATIC_FILE.ICON_LOGO}" 
                    } 
                  } 
                } 
              `
          }}
        />

        {/* --------------------------- Social embed script -------------------------- */}
        {hasInstagramEmbed && <script async defer src='//www.instagram.com/embed.js' />}
      </Head>
      <div>{dataLandingPage && <SectionLandingPageForArticle data={dataLandingPage} />}</div>
      {!isEmpty(dataAffiliate) && <SectionAffiliateLazy position={4} data={dataAffiliate} />}
      <Script id='script-creden' strategy='lazyOnload' src='https://data.creden.co/js/detect_company_name.js?client_name=bangkokbiznews' />
      <Layout ads={ads} sessionSevId={sessionSevId} navData={navData}>
        {/* ---------------------------- ADS : LIGHTBOX ----------------------------- */}
        {_article?.is_show_ads && !isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.LIGHTBOX} data={ads} handler={() => handleLightBoxClick()} adsBox={adsBox} />}

        <PageWrapper id='content-wrapper'>
          {/* ---------------------------- ADS : Content ----------------------------- */}
          {_article?.is_show_ads && !isEmpty(ads) && (
            <>
              <div ref={sentinelRef} />
              <AdsReserveSpace
                className='block-ads-billboard'
                ref={adsRef}
                $position={isDesktop && isStickyAdCategory && !isReleased}
                $pinned={isStuck && !isReleased}
                $fixedHeight={adsHeight}
                $topOffset={stickyMenuHeight}
              >
                <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />
              </AdsReserveSpace>
            </>
          )}
          <ArticleDefaultLazy
            ads={ads}
            dataAdsNative={dataAdsNative}
            dataAffiliate={dataAffiliate}
            lastNews={responsive?.isMobile ? lastNews?.slice(0, 3) : lastNews}
            isKeyPoint={isKeyPoint}
            related={related}
            translated={
              isTranslated === true && (
                <WidgetStickyContentsLazy
                  className='block-sticky'
                  isLoading={isLoading}
                  article={article}
                  handleTranslate={handleTranslate}
                  selectLang={selectLang}
                  router={router}
                  isTranslated={isTranslated}
                />
              )
            }
            article={_article}
          />
        </PageWrapper>

        {/* ---------------------------- ADS : LIGHTBOX ----------------------------- */}
        {_article?.is_show_ads && !isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.AD1X1} data={ads} />}
        {/* {_article?.is_show_ads && !isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.LIGHTBOX} data={ads} handler={() => handleLightBoxClick()} adsBox={adsBox} />} */}
      </Layout>
    </>
  );
};

const WidgetStickyPlaceholder = styled.div`
  min-height: 72px;
  @media (max-width: 1024px) {
    min-height: 0;
  }
`;

const PageWrapper = styled.div`
  background-color: var(--background-color);
  margin: 0;
  padding-bottom: 30px;
  @media (max-width: 767px) {
    padding-top: 90px;
  }
`;

const AdsReserveSpace = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-height: 300px;
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(245, 245, 245);
  & .block-ads-billboard {
    width: 100%;
    min-height: 300px;
  }

  @media (max-width: 767px) {
    min-height: 350px;
    & .block-ads-billboard {
      min-height: 350px;
    }
  }

  ${({ $position, $topOffset }) =>
    $position &&
    `
      position: sticky;
      top: ${$topOffset}px;
      z-index: 9;
    `}
  ${({ $fixedHeight }) =>
    $fixedHeight &&
    `
      min-height: ${$fixedHeight}px;

      & .block-ads-billboard {
        min-height: ${$fixedHeight}px;
      }
    `}

  ${({ $pinned }) =>
    $pinned &&
    `
    background: none;

    & .block-ads-billboard {
      background: none;
    }
  `}
`;

async function getNavigationData() {
  try {
    const initialNavData = await GET('/api/v1.0/navigations/menu-nav');
    if (!Array.isArray(initialNavData) || isEmpty(initialNavData)) return null;

    return await Promise.all(initialNavData.map(category => processCategory(category, API_URI)));
  } catch (err) {
    return null; // nav พังห้ามทำให้หน้าบทความพัง — ปล่อยให้ Layout fallback ไปยิงฝั่ง client
  }
}

export async function getServerSideProps({ req, res, params, query, resolvedUrl }) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    isTranslated: false,
    agent: null,
    related: null,
    lastNews: null,
    dataLandingPage: [],
    solrRelated: [],
    specialNews: false,
    dataAffiliate: [],
    dataAdsNative: null,
    ads: [],
    dataAdsHeader: null,
    navData: null,
    article: null
  };

  if (rewriteTxt(params.id) === 'latest-news') {
    return { redirect: { destination: `${APP_URL}/category/latest-news`, permanent: false } };
  }

  const { expires, signature } = query;
  let preview = '';
  let previewParams = {};
  if (expires && signature) {
    preview = '/preview';
    previewParams = { expires, signature };
  }

  const url = req.url;
  let uri = `contents/${rewriteTxt(params.id)}`;
  if (['/advertorials', '/recommended', '/pr-news', '/pr', '/blogs', '/blog'].some(p => url.startsWith(p))) {
    uri = query?.type ? `contents/${params.id}/${query?.type || ''}` : `contents/${params.id}`;
  }

  try {
    const [resData, scriptBrowser, landinPage, navData] = await Promise.all([
      GET_ARTICLES(`/api/v1.0/${uri}${preview}${convertObjPath({ ...previewParams })}`, false, true),
      GET('/api/headerAds').catch(() => null),
      GET('/api/landing-page/list').catch(() => null),
      getNavigationData()
    ]);

    if (resData?.statusCode === 404 || isUndefined(resData)) {
      return { notFound: true };
    }

    if (!isEmpty(scriptBrowser)) initProps.dataAdsHeader = scriptBrowser || '';
    if (!isEmpty(landinPage)) initProps.dataLandingPage = landinPage;
    initProps.navData = navData;

    if (!isEmpty(resData?.data)) {
      initProps.article = resData?.data;
      // Canonical URL redirect check (skip in preview mode)
      const isPreviewMode = !!(expires && signature);
      if (!isPreviewMode) {
        const canonical = resData.data?.canonical;
        if (canonical) {
          let canonicalPath;
          try {
            canonicalPath = canonical.includes('://') ? new URL(canonical).pathname : canonical;
          } catch {
            canonicalPath = canonical;
          }

          const segs = p => {
            let s = (p || '').split('?')[0];
            try {
              s = decodeURIComponent(s);
            } catch {}
            return s.split('/').filter(Boolean);
          };

          const c = segs(canonicalPath);
          const r = segs(resolvedUrl || req.url);

          let sameHost = true;
          let targetPath = canonicalPath;
          if (canonical.includes('://')) {
            try {
              const u = new URL(canonical);
              sameHost = u.host === req.headers.host;
              targetPath = u.pathname;
            } catch {
              sameHost = false;
            }
          }

          const mismatch = c.length > 0 && r.length > 0 && (c[0] !== r[0] || c[c.length - 1] !== r[r.length - 1]);

          // กันเด้งหาตัวเอง — เงื่อนไขสุดท้ายก่อนยิง 301
          const isSelf = segs(targetPath).join('/') === r.join('/');

          if (sameHost && mismatch && !isSelf) {
            res.setHeader('Location', canonical);
            res.setHeader('Cache-Control', 'no-store');
            res.statusCode = 301;
            res.end();
            return initProps;
          }
        }
      }

      initProps.agent = req.headers['user-agent'];
      initProps.related = resData?.related || [];
      initProps.solrRelated = resData?.solrRelated || [];
      initProps.lastNews = resData?.last_news || [];
      initProps.dataAffiliate = resData?.affiliate || [];
      initProps.ads = resData?.ads || [];
      initProps.dataAdsNative = resData?.adsNative || {};
      initProps.isTranslated = resData?.is_translate || true;
      // initProps.isTranslated = resData?.is_translate ? resData?.is_translate : NEXT_PUBLIC_CLOSE_TRANSLATE ? !envTranslate.includes(params.id) : true; // resData?.is_translate || {};
    }
  } catch (err) {
    initProps.errMessage = err.message;
    initProps.statusCode = err?.response?.status || 500;
  }

  return { props: initProps };
}

export default PageCategoryDetail;
